import { useState, type ReactElement } from 'react';
import { Clip, grafikEbene, type Op, TextStyle } from '@schnitt/shared';
import { useEditor } from '../store';
import { newId } from '../ids';
import { useIstAdmin } from '../istAdmin';
import { Sheet, Slider } from './Sheet';

/**
 * Text and branded elements, at the playhead: on a phone the useful default
 * is "put it where I am looking". Auf welche Ebene, sagt `grafikEbene()`:
 * die oberste, wenn dort Platz ist, sonst eine neue darüber (Sascha,
 * 12. Oktober: „Marken Sets lassen sich nicht einfügen“ -- sie lagen unter
 * dem Video, das schon auf „Overlay“ stand).
 */

/** Was in einem Feld der Vorlage steht, in Worten statt als Kennung. */
const FELDNAMEN: Record<string, string> = {
  name: 'Name',
  role: 'Funktion',
  title: 'Titel',
  subtitle: 'Untertitel',
  headline: 'Überschrift',
  text: 'Text',
};
const feldname = (slot: string): string => FELDNAMEN[slot] ?? slot.charAt(0).toUpperCase() + slot.slice(1);
export function BrandSheet({
  onClose,
  onEditKit,
  onAssistent,
}: {
  onClose: () => void;
  onEditKit: () => void;
  /** Ein neues Markenset in vier Schritten (7.167). */
  onAssistent?: () => void;
}): ReactElement {
  const timeline = useEditor((s) => s.timeline);
  const istAdmin = useIstAdmin();
  const playhead = useEditor((s) => s.playhead);
  const dispatch = useEditor((s) => s.dispatch);
  const brandKit = useEditor((s) => s.project?.brandKit);
  const clipById = useEditor((s) => s.clipById);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  const toast = useEditor((s) => s.toast);

  const selected = selection.length === 1 ? clipById(selection[0]!) : null;
  // Narrow once and carry the source along: TypeScript cannot re-narrow a
  // union through a variable at every use site.
  const selectedText =
    selected && selected.clip.source.kind === 'text' ? { clip: selected.clip, source: selected.clip.source } : null;
  const selectedBrand =
    selected && selected.clip.source.kind === 'brand' ? { clip: selected.clip, source: selected.clip.source } : null;

  const [text, setText] = useState('Neuer Text');

  /** Die Ebene für eine Grafik -- und die neue Spur dafür, wenn es eine braucht. */
  const ebene = (dauer: number): { trackId: string; ops: Op[]; neu: boolean } => {
    const ziel = grafikEbene(timeline.tracks, playhead, dauer);
    return {
      trackId: ziel.trackId,
      ops: ziel.neueSpur ? [{ op: 'addTrack', track: ziel.neueSpur.track, index: ziel.neueSpur.index } as Op] : [],
      neu: Boolean(ziel.neueSpur),
    };
  };

  const addText = () => {
    const id = newId('t');
    const ziel = ebene(3);
    dispatch([...ziel.ops, {
      op: 'addClip',
      trackId: ziel.trackId,
      clip: Clip.parse({
        id,
        source: { kind: 'text', text, style: TextStyle.parse({ size: 0.06, align: 'center' }) },
        start: playhead,
        duration: 3,
        fadeIn: 0.3,
        fadeOut: 0.3,
        transform: { x: 0.5, y: 0.5, scale: 1, rotate: 0, opacity: 1 },
        label: text.split('\n')[0],
      }),
    } as Op]);
    select([id]);
    toast('success', ziel.neu ? 'Text eingefügt, auf einer neuen Ebene obenauf.' : 'Text eingefügt.');
  };

  const addTemplate = (templateId: string, name: string, defaultDuration: number) => {
    const id = newId('b');
    const ziel = ebene(defaultDuration);
    // Mit den Platzhaltern als Inhalt: leer zeigte eine frische Bauchbinde nur
    // einen leeren Rahmen, und das sah aus wie „nicht eingefügt“.
    const vorlage = brandKit?.templates.find((t) => t.id === templateId);
    const fields = Object.fromEntries(
      (vorlage?.layers ?? [])
        .filter((l): l is Extract<typeof l, { type: 'text' }> => l.type === 'text')
        .filter((l) => Boolean(l.placeholder))
        .map((l) => [l.slot, l.placeholder!]),
    );
    dispatch([...ziel.ops, {
      op: 'addClip',
      trackId: ziel.trackId,
      clip: Clip.parse({
        id,
        source: { kind: 'brand', templateId, fields },
        start: playhead,
        duration: defaultDuration,
        label: name,
      }),
    } as Op]);
    select([id]);
    toast('success', ziel.neu ? `„${name}“ eingefügt, auf einer neuen Ebene obenauf.` : `„${name}“ eingefügt.`);
  };

  const updateStyle = (values: Partial<ReturnType<typeof TextStyle.parse>>) => {
    if (!selectedText) return;
    dispatch({
      op: 'updateClip',
      clipId: selectedText.clip.id,
      patch: { source: { ...selectedText.source, style: { ...selectedText.source.style, ...values } } },
    } as Op);
  };

  return (
    <Sheet title="Text & Marke" onClose={onClose}>
      {selectedText ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Ausgewählter Text</h3>
          <label className="field">
            <span>Inhalt</span>
            <textarea
              value={selectedText.source.text}
              onChange={(e) =>
                dispatch({
                  op: 'updateClip',
                  clipId: selectedText.clip.id,
                  patch: {
                    source: { ...selectedText.source, text: e.target.value },
                    label: e.target.value.split('\n')[0]?.slice(0, 40) ?? '',
                  },
                } as Op)
              }
            />
          </label>
          <Slider
            label="Schriftgröße"
            value={selectedText.source.style.size}
            min={0.02}
            max={0.2}
            step={0.005}
            format={(v) => `${Math.round(v * 1000) / 10}%`}
            onChange={(v) => updateStyle({ size: v })}
          />
          <div className="slider-row">
            <label>Farbe</label>
            <input
              type="color"
              value={selectedText.source.style.color}
              onChange={(e) => updateStyle({ color: e.target.value })}
              style={{ flex: 1, height: 40, padding: 2 }}
            />
          </div>
          <div className="chips" style={{ marginBottom: 12 }}>
            <button className={`chip ${selectedText.source.style.bold ? 'active' : ''}`} onClick={() => updateStyle({ bold: !selectedText.source.style.bold })}>
              Fett
            </button>
            <button
              className={`chip ${selectedText.source.style.uppercase ? 'active' : ''}`}
              onClick={() => updateStyle({ uppercase: !selectedText.source.style.uppercase })}
            >
              GROSS
            </button>
            <button
              className={`chip ${selectedText.source.style.boxColor ? 'active' : ''}`}
              onClick={() => updateStyle({ boxColor: selectedText.source.style.boxColor ? null : '#0f172a' })}
            >
              Hinterlegt
            </button>
            <button
              className={`chip ${selectedText.source.style.outlineColor ? 'active' : ''}`}
              onClick={() =>
                updateStyle({ outlineColor: selectedText.source.style.outlineColor ? null : '#000000' })
              }
            >
              Kontur
            </button>
            {(['left', 'center', 'right'] as const).map((align) => (
              <button
                key={align}
                className={`chip ${selectedText.source.style.align === align ? 'active' : ''}`}
                onClick={() => updateStyle({ align })}
              >
                {{ left: '⇤', center: '↔', right: '⇥' }[align]}
              </button>
            ))}
          </div>

          {/* An outline is what makes white text readable over a bright sky,
              so its colour and thickness are worth reaching, not just its
              existence. */}
          {selectedText.source.style.outlineColor ? (
            <>
              <div className="slider-row">
                <label>Konturfarbe</label>
                <input
                  type="color"
                  value={selectedText.source.style.outlineColor}
                  onChange={(e) => updateStyle({ outlineColor: e.target.value })}
                  style={{ flex: 1, height: 40, padding: 2 }}
                />
              </div>
              <Slider
                label="Konturstärke"
                value={selectedText.source.style.outlineWidth}
                min={0.02}
                max={0.3}
                step={0.01}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(v) => updateStyle({ outlineWidth: v })}
              />
            </>
          ) : null}

          <label className="field">
            <span>Einblendung</span>
            <select
              value={selectedText.clip.animation}
              onChange={(e) =>
                dispatch({
                  op: 'updateClip',
                  clipId: selectedText.clip.id,
                  patch: { animation: e.target.value as typeof selectedText.clip.animation },
                } as Op)
              }
            >
              <option value="none">Ohne Bewegung</option>
              <option value="slideLeft">Von links hereinschieben</option>
              <option value="slideRight">Von rechts hereinschieben</option>
              <option value="slideUp">Von unten hereinschieben</option>
              <option value="slideDown">Von oben hereinschieben</option>
            </select>
            <p className="hint">
              Ein- und Ausblenden stellst du im Clip-Blatt ein.
            </p>
          </label>
          <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '18px 0' }} />
        </>
      ) : null}

      {selectedBrand ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Vorlage ausfüllen</h3>
          {(brandKit?.templates.find((t) => t.id === selectedBrand.source.templateId)?.layers ?? [])
            .filter((l): l is Extract<typeof l, { type: 'text' }> => l.type === 'text')
            .map((layer) => (
              <label className="field" key={layer.slot}>
                <span>{feldname(layer.slot)}</span>
                <input
                  value={selectedBrand.source.fields[layer.slot] ?? ''}
                  placeholder={layer.placeholder}
                  onChange={(e) =>
                    dispatch({
                      op: 'updateClip',
                      clipId: selectedBrand.clip.id,
                      patch: {
                        source: {
                          ...selectedBrand.source,
                          fields: { ...selectedBrand.source.fields, [layer.slot]: e.target.value },
                        },
                      },
                    } as Op)
                  }
                />
              </label>
            ))}
          <hr style={{ border: 0, borderTop: '1px solid var(--line)', margin: '18px 0' }} />
        </>
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Neu einfügen</h3>
      <label className="field">
        <span>Text</span>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} />
      </label>
      <button className="primary" onClick={addText} style={{ width: '100%', marginBottom: 16 }}>
        Text bei {playhead.toFixed(1)}s einfügen
      </button>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Vorlagen aus dem Markenset</h3>
      <div className="list">
        {(brandKit?.templates ?? []).map((template) => (
          <div className="row" key={template.id}>
            <div className="grow">
              <div className="title">{template.name}</div>
              <div className="sub">
                {{ lowerThird: 'Bauchbinde', title: 'Titelkarte', outro: 'Abspann', bug: 'Logo' }[template.kind]} ·{' '}
                {template.defaultDuration}s
              </div>
            </div>
            <button className="small primary" data-vorlage={template.id} onClick={() => addTemplate(template.id, template.name, template.defaultDuration)}>
              Einfügen
            </button>
          </div>
        ))}
      </div>
      {/* Ein neues Markenset legen nur Admins an (7.226). */}
      {onAssistent && istAdmin ? (
        <button className="primary" style={{ width: '100%', marginTop: 14 }} onClick={onAssistent} data-markenset-assistent>
          Neues Markenset in vier Schritten
        </button>
      ) : null}
      <button style={{ width: '100%', marginTop: 8 }} onClick={onEditKit} data-markenset-oeffnen>
        {istAdmin ? 'Markenset bearbeiten' : 'Markensets ansehen'}
      </button>
      <p className="hint">
        Farben, Schrift und Logo kommen aus dem Markenset des Projekts.
      </p>
    </Sheet>
  );
}
